import { upgradeClaritySession } from '@ifixit/analytics';
import { useCallback, useRef, useState } from 'react';
import { markAnswered, markDismissed, shouldAutoOpen } from './history-storage';
import { useFeedbackOpenRequest } from './open-request';
import type { FeedbackScore, FeedbackTransport } from './types';

export type FeedbackStep = 'vote' | 'text' | 'thanks';

interface FlowState {
   isOpen: boolean;
   openedAutomatically: boolean;
   step: FeedbackStep;
   voteId: number | null;
   score: FeedbackScore | null;
   isSending: boolean;
   hasError: boolean;
}

const CLOSED: FlowState = {
   isOpen: false,
   openedAutomatically: false,
   step: 'vote',
   voteId: null,
   score: null,
   isSending: false,
   hasError: false,
};

export function useFeedbackFlow(surface: string, transport: FeedbackTransport) {
   const [state, setState] = useState<FlowState>(CLOSED);
   const unansweredAutoOpenRef = useRef(false);
   const interactionRef = useRef(0);

   const openAutomatically = useCallback(() => {
      if (!shouldAutoOpen(surface)) {
         return;
      }

      setState(current => {
         if (current.isOpen) {
            return current;
         }
         unansweredAutoOpenRef.current = true;
         interactionRef.current += 1;
         return { ...CLOSED, isOpen: true, openedAutomatically: true };
      });
   }, [surface]);

   useFeedbackOpenRequest(surface, openAutomatically);

   const setOpen = useCallback(
      (isOpen: boolean) => {
         if (isOpen) {
            setState(current => {
               if (current.isOpen) {
                  return current;
               }
               interactionRef.current += 1;
               return { ...CLOSED, isOpen: true };
            });
            return;
         }

         if (unansweredAutoOpenRef.current) {
            markDismissed(surface);
         }
         unansweredAutoOpenRef.current = false;
         interactionRef.current += 1;
         setState(CLOSED);
      },
      [surface]
   );

   const vote = useCallback(
      async (score: FeedbackScore) => {
         const interaction = interactionRef.current;
         const isStale = () => interaction !== interactionRef.current;
         setState(current => ({ ...current, isSending: true, hasError: false, score }));
         try {
            const { voteId } = await transport.sendVote({ surface, score });
            markAnswered(surface);
            upgradeClaritySession('feedback-prompt');
            if (isStale()) {
               return;
            }
            unansweredAutoOpenRef.current = false;
            setState(current => ({
               ...current,
               isSending: false,
               voteId,
               step: score === 1 ? 'thanks' : 'text',
            }));
         } catch {
            if (!isStale()) {
               setState(current => ({ ...current, isSending: false, hasError: true, score: null }));
            }
         }
      },
      [surface, transport]
   );

   const sendText = useCallback(
      async (text: string) => {
         const voteId = state.voteId;
         if (voteId === null) {
            return;
         }

         const interaction = interactionRef.current;
         const isStale = () => interaction !== interactionRef.current;
         setState(current => ({ ...current, isSending: true, hasError: false }));
         try {
            await transport.sendText({ surface, voteId, text });
            if (!isStale()) {
               setState(current => ({ ...current, isSending: false, step: 'thanks' }));
            }
         } catch {
            if (!isStale()) {
               setState(current => ({ ...current, isSending: false, hasError: true }));
            }
         }
      },
      [surface, transport, state.voteId]
   );

   return {
      isOpen: state.isOpen,
      step: state.step,
      isSending: state.isSending,
      hasError: state.hasError,
      setOpen,
      vote,
      sendText,
   };
}
